import React, { useState, useRef } from "react";
import { toast } from "sonner";
import type { CsvUploadResult } from "../types";

interface BulkRemoveSectionProps {
  onUpload: (file: File) => void;
  isUploading: boolean;
  uploadResult: CsvUploadResult | null;
}

const BulkRemoveSection: React.FC<BulkRemoveSectionProps> = ({
  onUpload,
  isUploading,
  uploadResult,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && !file.name.endsWith(".csv")) {
      toast.error("Please select a CSV file");
      e.target.value = "";
      return;
    }
    if (file && file.size > 10 * 1024 * 1024) {
      toast.error("File must be under 10MB");
      e.target.value = "";
      return;
    }
    if (file) {
      setSelectedFile(file);
      onUpload(file);
    }
  };

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  const handleDownloadSample = () => {
    const csvContent = "Phone\n7074567890\n5417543010\n8663625478";
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "sample_delete.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="card">
        <div className="card-header tc-card-header">
          <h5 className="mb-0" style={{ color: "white" }}>Bulk Remove Phone Numbers</h5>
        </div>
        <div className="card-body">
          <div className="row">
            {/* Left column — form */}
            <div className="col-md-6">
              <p className="fw-bold mb-1">Bulk Remove Phone Numbers from TrustCall</p>
              <p className="text-muted mb-3">
                <strong>CSV Format:</strong> Phone
              </p>

              {/* Hidden file input + styled button */}
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                style={{ display: "none" }}
                onChange={handleFileChange}
              />
              <div className="d-flex align-items-center gap-2 mb-3">
                <button
                  className="btn"
                  style={{ backgroundColor: "var(--navbar-and-treeview-bg)", borderColor: "var(--navbar-and-treeview-bg)", color: "#fff" }}
                  onClick={handleChooseFile}
                  disabled={isUploading}
                >
                  <svg viewBox="0 0 512 512" width="16" height="16" fill="currentColor" className="me-1" style={{ verticalAlign: "middle" }}>
                    <path d="M32 384v96h448v-96zm192-64h64V192h96L256 32 128 192h96z" />
                  </svg>
                  <span style={{ verticalAlign: "middle" }}>Choose CSV File</span>
                </button>
                {selectedFile && (
                  <span className="text-muted">{selectedFile.name}</span>
                )}
              </div>

              {/* Upload Result */}
              {uploadResult && (
                <div className="alert alert-info mt-3">
                  <strong>Result:</strong> {uploadResult.removed} removed,{" "}
                  {uploadResult.skipped} skipped
                  {uploadResult.errors.length > 0 && (
                    <div className="mt-2">
                      <strong>Errors ({uploadResult.errors.length}):</strong>
                      <ul className="mb-0 small">
                        {uploadResult.errors.slice(0, 10).map((err, i) => (
                          <li key={i}>
                            Line {err.line}: {err.phone} — {err.reason}
                          </li>
                        ))}
                        {uploadResult.errors.length > 10 && (
                          <li>...and {uploadResult.errors.length - 10} more</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Right column — File Format Instructions (matches MVC: lightgrey rounded box + h4) */}
            <div className="col-md-6">
              <div className="tc-file-format-box" style={{ padding: "0.8rem", borderRadius: "0.8rem" }}>
                <div style={{ marginBottom: 5 }}>
                  <h4>File Format Instructions</h4>
                  <p>
                    Filenames must end with a .csv extension and have a header row as shown below.
                    There must not be any more content than a column for Phone.
                  </p>
                  <p>
                    Phone numbers should be 10 digits with no formatting characters or leading 1s
                  </p>
                </div>

                <table className="table table-bordered table-sm tc-table-bordered">
                  <thead>
                    <tr>
                      <th scope="col">Phone</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>7074567890</td>
                    </tr>
                    <tr>
                      <td>5417543010</td>
                    </tr>
                    <tr>
                      <td>8663625478</td>
                    </tr>
                  </tbody>
                </table>

                {/* DNC-390: this download is a read and must stay usable for Agents, but
                    ActivePhones wraps the section in <fieldset disabled>. Two things fight
                    that: the fieldset itself (so this is an <a>, which the HTML spec does
                    not disable) and Bootstrap's `fieldset:disabled .btn { pointer-events:
                    none }`, which does hit anchors — hence the inline escape below, since
                    inline styles outrank the stylesheet. Removing either breaks the link. */}
                <a
                  href="#"
                  className="btn btn-sm"
                  style={{ width: 200, backgroundColor: "var(--navbar-and-treeview-bg)", borderColor: "var(--navbar-and-treeview-bg)", color: "#fff", pointerEvents: "auto", opacity: 1 }}
                  onClick={(e) => {
                    e.preventDefault();
                    handleDownloadSample();
                  }}
                >
                  <svg viewBox="0 0 512 512" width="14" height="14" fill="currentColor" className="me-1" style={{ verticalAlign: "middle" }}>
                    <path d="M416 32V0h-32v32h-32V0h-32v32h-32V0h-32v32h-32V0h-32v32h-32V0h-32v32H96V0H64v32H32v32H0v32h32v32H0v32h32v32H0v32h32v32H0v32h32v32H0v32h32v32H0v32h32v32h32v32h32v-32h32v32h32v-32h32v32h32v-32h32v32h32v-32h32v32h32v-32h32v32h32v-32h32v-32h32v-32h-32v-32h32v-32h-32v-32h32v-32h-32v-32h32v-32h-32v-32h32v-32h-32V96h32V64h-32V32zm0 384H64V64h352zm32 64v-32h32v32zm32-448h-32V0h32zM0 448h32v32H0zM32 0v32H0V0zm160 128H96v32h96zm192 0H224v32h160zm0 64H224v32h160zm0 64H224v32h160zm0 64H224v32h160zm-192 0H96v32h96zm0-64H96v32h96zm0-64H96v32h96z" />
                  </svg>
                  <span style={{ verticalAlign: "middle" }}>Download Example File</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkRemoveSection;
